import styles from './LocationSelectDialog.module.css'
import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import Popper from '@mui/material/Popper'
import { NearbyButton } from './NearbyButton'
import { ClearAllButton } from './ClearAllButton'
import { SearchButton } from './SearchButton'
import { useAdvancedLocation } from 'shared/hooks/useAdvancedLocation/useAdvancedLocation'
import { useBreakpointUp } from 'shared/hooks/useBreakpointQueries'
import { ClickAwayListener, Dialog, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useDeleteLocationsTipsMutation } from 'entities/locationTips/api/locationTipsApi'
import { useLazyReadAutoCompleteLocationsQuery } from 'entities/autoCompleteLocations/api/autoCompleteLocationsApi'
import { useDebouncedCallback } from 'use-debounce'
import { SpyLaunchSettingsPanel } from './SpyLaunchSettingsPanel'
import { FilterPanel } from './FilterPanel'
import { LocationPanel } from './LocationPanel/LocationPanel'
import { LocationAutoCompleteInput } from 'shared/widgets/LocationAutoCompleteInput'
import { LocationSearchHistory } from './LocationSearchHistory'

interface LocationSelectProps{
    location: any
    //locationSearchString: string
    filter: any
    spyLaunchSettings: any
    className?: string
    open: boolean
    onOpenChange?: (event: any) => void
    onClose?: (event: any) => void
    onSearch?: (event: any) => void
    onLocationChange?: (event: any) => void
    onLocationSearchStringChange?: (event: any) => void
    onSpyLaunchSettingsChange?: (event: any) => void
    onFiltersChange?: (event: any) => void
}

export const LocationSelectDialog = (props: LocationSelectProps): JSX.Element => {
    const {
        location,
        // locationSearchString,
        filter,
        spyLaunchSettings,
        className,
        open,     
        onOpenChange,
        onSearch,
        onLocationChange,
        onLocationSearchStringChange,
        onSpyLaunchSettingsChange,
        onFiltersChange,
        onClose,
    } = props

    // const [location, setLocation] = useState<any>(undefined)
    const [locationSearchString, setLocationSearchString] = useState<any>('')
    const [locationAutocompleteInputError, setLocationAutocompleteInputError] = useState<any>(undefined)
    const [locationString, setLocationString] = useState<string>(``)
    const [locationOptions, setLocationOptions] = useState<any>([])
    const [locationOptionsIsLoading, setLocationOptionsIsLoading] = useState<boolean>(false)
    const [locationOptionsIsOpen, setLocationOptionsIsOpen] = useState<boolean>(false)
    const [searchInputRef, setSearchInputRef] = useState<HTMLButtonElement | null>(null)
    const {
        location: locationFromBrowser,
        loading,
        error,
        permission,
        permissionLoading,
        isWatching,
        requestLocation,
        requestPermission,
        startWatching,
        stopWatching,
        clearError,
        clearLocation
    } = useAdvancedLocation()
    const isXlWidthScreenOrMore = useBreakpointUp('xl')
    const [deleteLocationTips] = useDeleteLocationsTipsMutation()
    const [lazyReadAutoCompleteLocationsQuery] = useLazyReadAutoCompleteLocationsQuery()
    const [panelsExpanded, setPanelsExpanded] = useState({
        spyLaunchSettingsPanel: false,
        filtersPanel: false,
        locationPanel: false
    })

    const searchButtonRef = useCallback((node: any) => {
        if (node === null) {
            return
        }

        setSearchInputRef(node)
        node.focus()
    }, [])

    const popperOptions: any = {
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 40],
                },
            },
        ]
    }

    const anchorVirtualElement: any = {
        getBoundingClientRect: () => {
            return {
                width: window.innerWidth,
                height: 0,
                top: 90,
                right: 0,
                bottom: 0,
                left: 0,
            }
        },
    }


    const performSetLocation = (event: any) => {
        setLocationString(event.name)
        setLocationSearchString(event.name)
        onLocationChange?.(event)
    }

    const performSetFilters = (event: any) => {
        onFiltersChange?.(event)
    }

    const performSpyLaunchSettingsChange = (event: any) => {
        onSpyLaunchSettingsChange?.(event)
    }

    const handleHistoryItemSelect = (event: any) => {
        performSetLocation(event)
    }

    const handleSearchInputValueChange = (event: any) => {
        performSetLocation(event)
    }

    const handleNearbyButtonClick = (event: any) => {
        requestPermission()
    }

    const handleFilterPanelFiltersChange = (event: any) => {
        performSetFilters(event)
    }

    const handleSubmitForm = (event: any) => {
        event.preventDefault()
        if (location === undefined){
            setLocationAutocompleteInputError({
                text: 'Please select location'
            })
            return
        }

        onSearch?.({
            location: location,
            locationString: locationString
        })
    }

    const locationSelectClasses = classNames(styles.LocationSelect, className)

    useEffect(() => {
        const handlePermissionChange = async () => {
            if (permission === 'granted') {
                await requestLocation({
                    enableHighAccuracy: true,
                    timeout: 10000
                });
            }
        }

        handlePermissionChange()
    }, [
        permission,
        requestLocation
    ])

    useEffect(() => {
        const handleLocationChange = async () => {
            if (locationFromBrowser === null) {
                return
            }
        }

        handleLocationChange()
    }, [
        locationFromBrowser
    ])


    const handleDialogClose = (event: any) => {
        onClose?.(event)
    }

    const handleClearAllButtonClick = async () => {
        // await deleteLocationTips(undefined)
        // setLocationString(event.name)
        // setLocationSearchString(event.name)
        onLocationChange?.(undefined)
    }


    const debouncedGetLocationsOptions = useDebouncedCallback(
        async (event: any) => {
            onLocationChange?.(undefined)

            if (event === ``){
                setLocationOptions([])
                return
            }

            setLocationOptionsIsLoading(true)
            const lazyReadAutoCompleteLocationsQueryResult  = await lazyReadAutoCompleteLocationsQuery({query: event})

            if (lazyReadAutoCompleteLocationsQueryResult?.isError === true){
                setLocationOptions([])
                setLocationOptionsIsLoading(false)
                return
            }

            setLocationOptions(lazyReadAutoCompleteLocationsQueryResult.data.list)    
            setLocationOptionsIsLoading(false)
        },
        300
    )

    const handleAutocompleteInputInput = async (event: any) => {
        setLocationAutocompleteInputError(undefined)
        setLocationString(event)
        setLocationOptionsIsOpen(true)
        debouncedGetLocationsOptions(event)
    }

    const handleAutocompleteInputOptionClick = (event: any) => {
        setLocationAutocompleteInputError(undefined)
        setLocationOptionsIsOpen(false)
        performSetLocation(event)    
    }

    const handleClickAwayListenerClickAway = (event: any) => {
          onClose?.(event)
    }

    const handleLocationPanelLocationChange = (event: any) => {
        performSetLocation(event)
    }

    const handleSpyLaunchSettingsPanelSpyLaunchSettingsChange = (event: any) => {
        performSpyLaunchSettingsChange(event)
    }

    if (isXlWidthScreenOrMore) {
        return (
            <div className={locationSelectClasses}>
                <Popper
                    className={styles.Popper}
                    open={open}
                    anchorEl={anchorVirtualElement}
                    popperOptions={popperOptions}               
                >
                    <ClickAwayListener onClickAway={handleClickAwayListenerClickAway}>
                         <form className={styles.Popper__Form} onSubmit={handleSubmitForm}>
                            <LocationAutoCompleteInput 
                                text={locationString}
                                ref={searchButtonRef}
                                options={locationOptions}
                                isLoading={locationOptionsIsLoading}
                                isOptionsOpen={locationOptionsIsOpen}
                                error={locationAutocompleteInputError}
                                onInput={handleAutocompleteInputInput}
                                onOptionClick={handleAutocompleteInputOptionClick}
                            />
                                {/* <div className={styles.Popper__NearbyButtonContainer}>
                                    <NearbyButton
                                        className={styles.Popper__NearbyButton}
                                        onClick={handleNearbyButtonClick}
                                    />
                                </div> */}
                                {/* <hr
                                    className={styles.Popper__Divider}
                                /> */}
                            <div className={styles.Popper__HistoryContainer}>
                                <LocationSearchHistory
                                    onHistoryItemSelect={handleHistoryItemSelect}
                                />
                            </div>

                            <div className={styles.Popper__BottomToolbar}>
                                {/* <ClearAllButton 
                                    onClick={handleClearAllButtonClick}
                                /> */}
                                {/* <SearchButton
                                />                        */}
                            </div>
                        </form>
                    </ClickAwayListener>         
                </Popper>
            </div>
        )
    }

    //


    const handleSpyLaunchSettingsPanelSummaryClick = (event: any) => {
        setPanelsExpanded((prev: any) => {
            return {
                spyLaunchSettingsPanel: !prev.spyLaunchSettingsPanel,
                filtersPanel: false,
                locationPanel: false
            }
        })
    }

    const handleLocationPanelSummaryClick = (event: any) => {
        setPanelsExpanded((prev: any) => {
            return {
                locationPanel: !prev.locationPanel,
                spyLaunchSettingsPanel: false,
                filtersPanel: false
            }
        })
    }
    

    const handleFilterPanelSummaryClick = (event: any) => {
        setPanelsExpanded((prev: any) => {
            return {
                 locationPanel: false,
                spyLaunchSettingsPanel: false,
                filtersPanel: !prev.filtersPanel
            }
        })
    }


    const dialogSpyLaunchSettingsButtonContainerClasses = classNames(styles.Dialog__SpyLaunchSettingsButtonContainer, {})

    const handleSearchButtonClick = (event: any) => {
        onSearch?.({
            location,
            locationSearchString,
            filters: filter,
            spyLaunchSettings
        })
    }

    const handleLocationPanelLocationSearchStringChange = (event: any) => {
        setLocationSearchString(event)
    }

    return (
        <Dialog
            onClose={handleDialogClose}
            open={open}
            fullScreen
        >
            <div>
                <div className={styles.Dialog__Content}>    
                   <div className={styles.Dialog__StartContainer}>
                        <div></div>
                        <div className={styles.Dialog__TitleContainer}>
                            <div className={styles.Dialog__Title}>Search Competitors</div>
                        </div>                 
                        <div>
                            <IconButton
                                className={styles.CloseSearchButton}
                                aria-label=""
                                size="small"
                                onClick={handleDialogClose}
                            >
                                <CloseIcon
                                    className={styles.CloseSearchButton__Icon}
                                />
                            </IconButton>
                        </div>
                    </div>
                    <div className={styles.Dialog__CenterContainer}>
                        <div className={dialogSpyLaunchSettingsButtonContainerClasses}>
                            <LocationPanel
                                location={location}
                                expanded={panelsExpanded['locationPanel']}
                                onSummaryClick={handleLocationPanelSummaryClick}
                                searchString={locationSearchString}
                                onLocationSearchStringChange={handleLocationPanelLocationSearchStringChange}
                                onLocationChange={handleLocationPanelLocationChange}
                            />  
                        </div>     
                        <div className={dialogSpyLaunchSettingsButtonContainerClasses}>
                            <SpyLaunchSettingsPanel
                                spyLaunchSettings={spyLaunchSettings}
                                expanded={panelsExpanded['spyLaunchSettingsPanel']}
                                onSummaryClick={handleSpyLaunchSettingsPanelSummaryClick}
                                onSpyLaunchSettingsChange={handleSpyLaunchSettingsPanelSpyLaunchSettingsChange}
                            /> 
                        </div>
                        <div className={styles.Dialog__FilterButtonContainer}>
                            <FilterPanel 
                                filters={filter}
                                expanded={panelsExpanded['filtersPanel']}
                                onSummaryClick={handleFilterPanelSummaryClick}
                                onFiltersChange={handleFilterPanelFiltersChange}
                            /> 
                        </div>
                    </div>
                    <div className={styles.Dialog__EndContainer}>
                        {/* <ClearAllButton 
                            onClick={handleClearAllButtonClick}
                        /> */}
                        <SearchButton
                            onClick={handleSearchButtonClick}
                        />
                    </div>
                </div>    
            </div>
        </Dialog>
    )
}